import { Component, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { computeDiffs, revertDiffs, snapshotClone } from '../contest-sheets/native-diff.util';

/**
 * Native port of the AngularJS `ftPendingChangesPanel` deferred-save bar for the v1-look touchpoint
 * styling/messaging/campaign editors (reproduces the v1 `.ft-alert-section` markup for parity; uses
 * native-diff instead of the v1 `changesTracker`/`ft-snapshot`). The host feeds the live model root
 * and calls {@link recompute} after each field-editor modal closes. Save → `(save)` then {@link commit}
 * re-snapshots; Cancel → `revertDiffs` rollback + `(cancelled)`. `inactive` disables the Save button.
 */
@Component({
    selector: 'ft-pending-changes-panel-ng',
    imports: [TranslateModule],
    templateUrl: './ft-pending-changes-panel.component.html'
})
export class FtPendingChangesPanelComponent {
    /** The LIVE model root to track (e.g. `model.styling` or `model.bookingEngines`). */
    readonly live = input<any>();
    /** Disable the Save button (v1 `inactive` = form invalid). */
    readonly inactive = input(false);
    /** Emits the filtered diffs on Save (the host runs its v1 commit/saveData, then calls commit()). */
    readonly save = output<any[]>();
    /** Emitted after a Cancel rollback. */
    readonly cancelled = output<void>();

    protected readonly pendingChanges = signal(0);
    private snapshot: any = null;

    /**
     * Take the open-time snapshot (v1 ft-snapshot `take`). Call when the editor sheet mounts/loads.
     * `liveOverride` lets a host snapshot a JUST-reassigned root (e.g. a fresh server response after
     * save) before Angular has re-bound the `[live]` input on the next CD — without it, a re-snapshot
     * would baseline the STALE pre-save object and phantom pending-changes would reappear.
     */
    setup(liveOverride?: any): void {
        this.snapshot = snapshotClone(liveOverride !== undefined ? liveOverride : this.live());
        this.pendingChanges.set(0);
    }

    /** Snapshot ONLY if none yet — call at first field-open so the baseline is the stable, fully-loaded model. */
    setupIfNeeded(): void {
        if (!this.snapshot) this.setup();
    }

    /** Re-run the native diff (the host calls this after a native field modal closes). */
    recompute(): void {
        this.pendingChanges.set(this.filteredDiffs().length);
    }

    protected onSave(): void {
        // v1 fidelity: the AngularJS ftPendingChanges `.save` click handler is bound UNCONDITIONALLY and
        // fires regardless of `inactive` (pendingChanges.js:140) — `inactive` only toggles the `.disabled`
        // CSS class, and `.ft-btn.disabled` has NO pointer-events:none (opacity:.3 + cursor:not-allowed),
        // so a click on the dimmed button STILL saves. So NO hard guard here (the button dims via
        // [class.disabled] but a click still emits) — matches v1's save-anyway behaviour.
        this.save.emit(this.filteredDiffs());
    }

    protected onCancel(): void {
        if (!this.snapshot) {
            this.pendingChanges.set(0);
            this.cancelled.emit();
            return;
        }
        revertDiffs(this.live(), this.snapshot, computeDiffs(this.snapshot, this.live()));
        this.pendingChanges.set(0);
        this.cancelled.emit();
    }

    /** After a successful host save, re-snapshot to current (v1 `snapshot.commit()` → pendingChanges 0).
     * Pass `liveOverride` when the host has just reassigned the tracked root to a fresh object. */
    commit(liveOverride?: any): void {
        this.setup(liveOverride);
    }

    /** The current filtered diffs, for hosts that derive validity from the pending set
     * (v1 exposed the tracker's pendingChanges to the hidden `emailData` validation form). */
    currentDiffs(): any[] {
        return this.filteredDiffs();
    }

    /** The diffs that COUNT — verbatim v1 pendingChanges.js `changesFilter`. */
    private filteredDiffs(): any[] {
        if (!this.snapshot) return [];
        return computeDiffs(this.snapshot, this.live()).filter((c: any) => changesFilter(c));
    }
}

// --- verbatim v1 pendingChanges.js change filters (skip empty/formatting-only changes) ---
function changeIsDelete(change: any): boolean {
    return change.kind === 'D';
}
function changeIsDeleteInArray(change: any): boolean {
    return change.kind === 'A' && change.item && change.item.kind === 'D';
}
function isObject(v: any): boolean {
    return v !== null && typeof v === 'object';
}
function isEmptyObj(v: any): boolean {
    if (!isObject(v)) return true;
    for (const k in v) if (Object.prototype.hasOwnProperty.call(v, k)) return false;
    return true;
}
function changeIsNotEmptyObject(change: any): boolean {
    return (change.kind === 'N' || change.kind === 'E') && isObject(change.rhs) && isEmptyObj(change.rhs) === false;
}
function changeIsSimpleType(change: any): boolean {
    return (change.kind === 'N' || change.kind === 'E') && (typeof change.rhs === 'string' || typeof change.rhs === 'number' || typeof change.rhs === 'boolean');
}
function changeIsNotEmptyValueInArray(change: any): boolean {
    if (!(change.kind === 'A' && change.item && change.item.kind === 'N')) return false;
    const rhs = change.item.rhs;
    const objNoDollar =
        isObject(rhs) && Object.keys(rhs).filter((k) => k.toString().indexOf('$') !== 0).length === 0;
    return objNoDollar || (isObject(rhs) && isEmptyObj(rhs) === false) || typeof rhs === 'string' || typeof rhs === 'number' || typeof rhs === 'boolean';
}
function changesFilter(change: any): boolean {
    return (
        changeIsDelete(change) ||
        changeIsDeleteInArray(change) ||
        changeIsNotEmptyObject(change) ||
        changeIsSimpleType(change) ||
        changeIsNotEmptyValueInArray(change)
    );
}
